/**
 * Base API Client
 *
 * Generic authenticated REST API client for Firebase Cloud Functions.
 * Handles ID token authentication, URL construction, and error handling.
 *
 * Replaces httpsCallable pattern with REST API pattern for function consolidation.
 */

import { auth } from '../firebase';
import { kioskAuth } from '../kioskFirebase';

/**
 * Build the base URL for a Firebase Function HTTP endpoint
 */
export function getFunctionBaseUrl(functionName: string): string {
  const useEmulators = import.meta.env.VITE_USE_EMULATORS === '1';
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const region = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'europe-west3';

  if (useEmulators) {
    const host = import.meta.env.VITE_EMU_HOST || '127.0.0.1';
    const port = import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT || '5001';
    return `http://${host}:${port}/${projectId}/${region}/${functionName}`;
  }

  return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
}

/**
 * API Error with structured response
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Get the current user's Firebase ID token
 */
async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError('Not authenticated', 'unauthenticated', 401);
  }
  return user.getIdToken(true); // Force refresh to get latest claims
}

/**
 * Make an authenticated API request
 */
export async function apiRequest<T>(
  functionName: string,
  path: string,
  options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    body?: unknown;
    params?: Record<string, string | number | boolean | undefined>;
  }
): Promise<T> {
  const baseUrl = getFunctionBaseUrl(functionName);
  const token = await getIdToken();

  // Build URL with query params
  const url = new URL(`${baseUrl}${path}`);
  if (options.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  // Make request
  const response = await fetch(url.toString(), {
    method: options.method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  // Parse response
  const data = await response.json().catch(() => ({}));

  // Handle errors
  if (!response.ok) {
    // Extract user-friendly error message
    let errorMessage = data.message || data.error || 'Request failed';

    // Handle Zod validation errors (from Express routers)
    if (data.error === 'Validation error' && data.details && Array.isArray(data.details)) {
      // Format Zod issues into readable message
      const issues = data.details.map((issue: { path?: (string | number)[]; message: string }) => {
        const path = issue.path?.join('.') || 'field';
        return `${path}: ${issue.message}`;
      }).join(', ');
      errorMessage = `Validation failed: ${issues}`;
    }

    throw new ApiError(
      errorMessage,
      data.error || 'unknown',
      response.status,
      data
    );
  }

  return data as T;
}

/**
 * Make an authenticated API request using the kiosk Firebase auth session.
 * Used by /worker/* endpoints — ensures the kiosk employee's identity is sent,
 * NOT the main app user (e.g., the owner/admin logged in on the same browser).
 */
export async function apiRequestKiosk<T>(
  functionName: string,
  path: string,
  options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    body?: unknown;
    params?: Record<string, string | number | boolean | undefined>;
  }
): Promise<T> {
  const user = kioskAuth.currentUser;
  if (!user) {
    throw new ApiError('Kiosk session expired', 'unauthenticated', 401);
  }
  const token = await user.getIdToken(true);

  const baseUrl = getFunctionBaseUrl(functionName);
  const url = new URL(`${baseUrl}${path}`);
  if (options.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  const response = await fetch(url.toString(), {
    method: options.method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    let errorMessage = data.message || data.error || 'Request failed';
    if (data.error === 'Validation error' && data.details && Array.isArray(data.details)) {
      const issues = data.details.map((issue: { path?: (string | number)[]; message: string }) => {
        const path = issue.path?.join('.') || 'field';
        return `${path}: ${issue.message}`;
      }).join(', ');
      errorMessage = `Validation failed: ${issues}`;
    }
    throw new ApiError(errorMessage, data.error || 'unknown', response.status, data);
  }

  return data as T;
}

/**
 * Make an unauthenticated API request (for kiosk terminal operations)
 */
export async function apiRequestNoAuth<T>(
  functionName: string,
  path: string,
  options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    body?: unknown;
    params?: Record<string, string | number | boolean | undefined>;
    headers?: Record<string, string>;
  }
): Promise<T> {
  const baseUrl = getFunctionBaseUrl(functionName);

  // Build URL with query params
  const url = new URL(`${baseUrl}${path}`);
  if (options.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  // Make request (no Authorization header)
  const response = await fetch(url.toString(), {
    method: options.method,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  // Parse response
  const data = await response.json().catch(() => ({}));

  // Handle errors
  if (!response.ok) {
    // Extract user-friendly error message
    let errorMessage = data.message || data.error || 'Request failed';

    // Handle Zod validation errors (from Express routers)
    if (data.error === 'Validation error' && data.details && Array.isArray(data.details)) {
      // Format Zod issues into readable message
      const issues = data.details.map((issue: { path?: (string | number)[]; message: string }) => {
        const path = issue.path?.join('.') || 'field';
        return `${path}: ${issue.message}`;
      }).join(', ');
      errorMessage = `Validation failed: ${issues}`;
    }

    throw new ApiError(
      errorMessage,
      data.error || 'unknown',
      response.status,
      data
    );
  }

  return data as T;
}
